// Real PDF + Drive filing for signed staff_documents (offer letter, job
// description, background check consent) — these previously existed ONLY as
// an HTML string in the database, unlike training certificates which already
// get this treatment. Runs client-side in the candidate's browser
// (HRDocSign.js, right after a successful sign), same pattern as
// trainingCertificatePdf.js's renderCourseCertificateBlob.
//
// The HTML content here is only ever authored by this codebase (see
// src/lib/onboardingTemplates.js and OnboardingPipeline.js's
// COMPLIANCE_DOCS) — a small, controlled set of tags (p/strong/ul/li/br) —
// so a full HTML-to-PDF engine is unnecessary; DOMParser + a targeted walk
// covers it exactly.
import React from 'react';
import { Document, Page, View, Text, pdf } from '@react-pdf/renderer';
import { styles, COLORS, Letterhead, ensureCursiveFont } from './pdfPrimitives';

// ── HTML (p/strong/ul/li/br only) -> react-pdf elements ──────────────────────

function inlineNodesToText(node, keyPrefix) {
  const out = [];
  node.childNodes.forEach((child, i) => {
    const key = `${keyPrefix}-${i}`;
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent) out.push(<Text key={key}>{child.textContent}</Text>);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (tag === 'strong' || tag === 'b') {
        out.push(<Text key={key} style={{ fontFamily: 'Helvetica-Bold' }}>{child.textContent}</Text>);
      } else if (tag === 'br') {
        out.push(<Text key={key}>{'\n'}</Text>);
      } else {
        out.push(...inlineNodesToText(child, key));
      }
    }
  });
  return out;
}

function blockNodesToPdf(rootEl) {
  const blocks = [];
  Array.from(rootEl.children).forEach((el, i) => {
    const tag = el.tagName.toLowerCase();
    const key = `block-${i}`;
    if (tag === 'p') {
      blocks.push(<Text key={key} style={styles.body}>{inlineNodesToText(el, key)}</Text>);
    } else if (tag === 'ul') {
      Array.from(el.children).filter(li => li.tagName.toLowerCase() === 'li').forEach((li, j) => {
        blocks.push(
          <View key={`${key}-li-${j}`} style={{ flexDirection: 'row', marginBottom: 2 }}>
            <Text style={{ ...styles.bullet, paddingLeft: 0, width: 10 }}>•</Text>
            <Text style={{ ...styles.bullet, paddingLeft: 0, flex: 1 }}>{inlineNodesToText(li, `${key}-li-${j}`)}</Text>
          </View>
        );
      });
    } else {
      // Unexpected tag — render its text content plainly rather than drop it silently.
      blocks.push(<Text key={key} style={styles.body}>{el.textContent}</Text>);
    }
  });
  return blocks;
}

function SignedDocumentPdf({ data }) {
  ensureCursiveFont();
  const { docLabel, staffName, contentHtml, candidateSignedName, candidateSignedAt, officeSignedBy, officeSignerRole, officeSignedAt, recordId } = data;
  const dom = new DOMParser().parseFromString(`<div>${contentHtml || ''}</div>`, 'text/html');
  const bodyBlocks = blockNodesToPdf(dom.body.firstElementChild || dom.body);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <Letterhead />
        <View style={{ marginBottom: 10 }}>
          <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 14, color: COLORS.navy }}>{docLabel}</Text>
          <Text style={{ fontSize: 8, color: COLORS.mid, marginTop: 2 }}>{staffName}</Text>
        </View>

        <View>{bodyBlocks}</View>

        <View style={{ marginTop: 24, flexDirection: 'row', justifyContent: 'space-between' }}>
          <View style={{ width: '46%' }}>
            <Text style={styles.sigLabel}>CANDIDATE SIGNATURE</Text>
            <Text style={styles.signature}>{candidateSignedName}</Text>
            <View style={styles.sigLine} />
            <Text style={styles.sigName}>{candidateSignedName} — {candidateSignedAt}</Text>
          </View>
          {officeSignedBy && (
            <View style={{ width: '46%' }}>
              <Text style={styles.sigLabel}>A HEART FOR CARE REPRESENTATIVE</Text>
              <Text style={styles.signature}>{officeSignedBy}</Text>
              <View style={styles.sigLine} />
              <Text style={styles.sigName}>{officeSignedBy}{officeSignerRole ? ` (${officeSignerRole})` : ''} — {officeSignedAt}</Text>
            </View>
          )}
        </View>

        <View style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 7, color: COLORS.mid }}>Signed electronically — ESIGN Act / UETA</Text>
          <Text style={{ fontSize: 7, color: COLORS.mid }}>Record ID: {recordId}</Text>
        </View>
      </Page>
    </Document>
  );
}

export async function renderSignedDocumentBlob(data) {
  return pdf(<SignedDocumentPdf data={data} />).toBlob();
}

export { blobToBase64 } from './trainingCertificatePdf';
